import React from "react";
import {Alert, Box} from "@mui/material";
import {DocHeaderTitle, MoreQuestionsFooterText} from "../shopify/HowToIntegrateSkroWithShopifyStore";
import {DocDivider, DocH2, DocImage, DocRoot} from "../Docs";

function RsocVisymoIntegration() {
    return (
        <DocRoot>
            <Box sx={{textAlign: "center"}}>
                <a href="https://visymo.com" target="_blank" rel="noreferrer">
                    <DocImage sx={{maxWidth: 150}} src="/images/integration/visymo.svg" alt="Visymo logo"/>
                </a>
            </Box>
            <DocHeaderTitle title="RSOC Visymo and Skro"/>
            <Box>
                <DocH2>Who we are</DocH2>
                <p>
                    <a href="https://visymo.com" target="_blank" rel="noreferrer">
                        Visymo
                    </a> Universal Search Group (since 1997) is the invisible power behind valuable search
                    results. Every single day, the search technology we developed in-house helps millions of users find
                    the relevant information they seek. In part, we are able to achieve this as a result of our
                    strategic partnerships with e.g. Google, Microsoft and Yahoo. What originally began in the
                    Netherlands is now available in virtually every country around the world. Visymo Universal Search
                    Group has more than forty international brands - and our expansion shows no signs of slowing down.
                </p>
                <DocH2>Integration benefits</DocH2>
                <p>
                    Integration enables seamless revenue data synchronization across channels, ensuring accurate
                    tracking even after adjustments from various traffic sources. It also consolidates your ads,
                    conversions, and revenue, giving you a clear and comprehensive view of your ad performance.
                </p>
                <ul>
                    <li>Revenue updates every hour.</li>
                    <li>Revenue updates for the last 3 days.</li>
                </ul>

                <DocH2>Step 1. Add integration</DocH2>
                <DocDivider/>
                <p>1. Log in to your <strong>Skro</strong> account.</p>
                <p>2. Navigate to <strong>Settings</strong> -> <strong>Integrations</strong>.</p>
                <p>
                    3. Scroll down and click on the <strong>Add</strong> button in <strong>Revenue
                    Sync - RSOC</strong> section.
                </p>
                <DocImage src="/images/docs/rsocvisymo/rsoc-visymo-int-1.png" alt="visymo RSOC integration step 1"
                />
                <p>4. Click on the <strong>Visymo</strong> logo.</p>
                <DocImage src="/images/docs/rsocvisymo/rsoc-visymo-int-2.png" alt="visymo RSOC integration step 2"
                />
                <ul>
                    <li><strong>Integration Name</strong> name of your integration.</li>
                    <li>
                        <strong>Partner Name</strong> is your Visymo partner name, (usually it's same as your username).
                    </li>
                    <li>
                        <strong>RSOC Username</strong> is your Visymo RSOC username.
                    </li>
                    <li>
                        <strong>RSOC Password</strong> is your Visymo RSOC password.
                    </li>
                    <li><strong>Account Currency</strong> is the currency of the earnings.</li>
                    <li>
                        <strong>Custom Conversion Event</strong> is not required field. If you are using Custom
                        Conversion on your postback URL, then you will need to select your event here, otherwise your
                        revenue isn't synced correctly.
                    </li>
                </ul>
                <DocImage src="/images/docs/rsocvisymo/rsoc-visymo-int-3.png" alt="visymo RSOC integration step 3"
                />

                <p>5. Fill the form and click <strong>Save & Close</strong></p>

                <DocH2>Step 2. Add Visymo RSOC offer in Skro</DocH2>
                <DocDivider/>

                <p>1. Go to <strong>Offers</strong> page.</p>
                <p>2. Click on <strong>Create</strong> button and fill all the fields where needed.</p>
                <p>
                    3. Map your <strong>RSOC Channel/Campaign ID</strong> on special field to track revenue
                    correctly. In this example it is <strong>in_vt_ch1896</strong>
                </p>

                <DocImage src="/images/docs/rsocvisymo/rsoc-visymo-int-4.png" alt="viysmo RSOC integration step 4"
                          sx={{mb: 3}}/>

                <p>This is an example offer URL which goes to Skro:</p>
                <Alert severity="success" variant="filled" color="success">
                    {`https://mydomain.info/dsr?sclid={clickId}&&asid=in_vt_ch1896&q=Therapy%20Assistant%20Programs&de=m&locale=en_US&rac=Therapy%20Assistant`}
                </Alert>

                <p>
                    Offer URL has 2 important parameters:
                </p>
                <p>
                    1. <strong>&asid=in_vt_ch1896</strong> - You should pass your Channel ID in this param. Without
                    correctly setting this parameter, Visymo is unable to track and payout revenue.
                </p>
                <p>
                    2. <strong>{`&sclid={clickId}`}</strong> - This is secondary click ID parameter, where you should
                    pass Skro Click ID to track conversions.
                </p>

                <Alert color="warning" variant="filled" severity="warning" sx={{mt: 2, mb: 2}}>
                    {`Note: If you are encoding Offer URL, do not encode Skro click id macro {clickId}`}
                </Alert>

                <DocH2>Step 3. Standard Postback URL for conversions</DocH2>
                <DocDivider/>
                <p>
                    1. Go to <strong>Affiliate Networks</strong> and click on <strong>Create</strong> button.
                </p>
                <p>
                    2. Search <strong>Visymo</strong> and click on <strong>Visymo logo</strong>.
                </p>
                <p>
                    3. Copy the postback URL.
                </p>
                <p>
                    4. Switch on <strong>Allow double conversions</strong>.
                </p>
                <p>5. If you are done then click on <strong>Save & Close</strong> button.</p>
                <DocImage src="/images/docs/rsocvisymo/rsoc-visymo-int-5.png" alt="visymo RSOC integration step 5"
                          sx={{mb: 1}}/>

                <p>
                    6. Give your Postback URL to your account manager. You can use your own custom tracking domain
                    instead of skrotrack.com. You don't need to map Skro click id, because they
                    are using fixed parameter names and this part is already done by the integration.
                </p>
                <Alert color="warning" variant="filled" severity="warning" sx={{mt: 2, mb: 2}}>
                    {`Postback URL: https://skrotrack.com/postback`}
                </Alert>

                <DocDivider/>
                <p>Integration setup is done and your are ready to sync your earnings data.</p>
            </Box>
            <MoreQuestionsFooterText/>
        </DocRoot>
    );
}

export default RsocVisymoIntegration;
